import {
  Sun, CloudSun, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning,
} from "lucide-react";

// 16 event colours — the palette from the reference "Color your events" screen.
export const EVENT_COLOURS = {
  red: "#ef4444", orange: "#f97316", amber: "#f59e0b", yellow: "#eab308",
  lime: "#84cc16", green: "#22c55e", teal: "#14b8a6", cyan: "#06b6d4",
  sky: "#0ea5e9", blue: "#2f7bff", indigo: "#6366f1", violet: "#8b5cf6",
  pink: "#ec4899", rose: "#f43f5e", brown: "#a1662f", slate: "#64748b",
};

export const COLOUR_KEYS = Object.keys(EVENT_COLOURS);

export const colourOf = (key) => EVENT_COLOURS[key] || EVENT_COLOURS.blue;

// Calendar-only colour themes.
export const THEMES = {
  electric: { label: "Electric", accent: "#2F7BFF" },
  mint: { label: "Mint", accent: "#38C9A9" },
  sunset: { label: "Sunset", accent: "#FF7A45" },
  grape: { label: "Grape", accent: "#A855F7" },
  blossom: { label: "Blossom", accent: "#F43F5E" },
  ink: { label: "Ink", accent: "#94A3B8" },
  emerald: { label: "Emerald", accent: "#22C55E" },
  lagoon: { label: "Lagoon", accent: "#06B6D4" },
  bubblegum: { label: "Bubblegum", accent: "#EC4899" },
  indigo: { label: "Indigo", accent: "#6366F1" },
};

export const THEME_KEYS = Object.keys(THEMES);

export const accentOf = (key) => (THEMES[key] || THEMES.electric).accent;

export const REPEAT_LABELS = {
  none: "Does not repeat", daily: "Every day", weekly: "Every week",
  monthly: "Every month", yearly: "Every year",
};

export const ALERT_LABELS = {
  none: "No alert",
  at_time: "At the time",
  "10m": "10 minutes before",
  "30m": "30 minutes before",
  "1h": "1 hour before",
  "1d": "1 day before",
  same_day: "Email me that morning",
  day_before: "Email me the day before",
};

// How many minutes before the start a browser notification should fire.
export const ALERT_MINUTES = { at_time: 0, "10m": 10, "30m": 30, "1h": 60, "1d": 1440 };

export const TASK_PRIORITIES = {
  high: { label: "High", tint: "#ef4444" },
  normal: { label: "Normal", tint: "#0ea5e9" },
  low: { label: "Low", tint: "#64748b" },
};

// 24h "14:30" → "2:30 pm" when the 12 hour clock is chosen.
export const fmtTime = (hhmm, clock = "24") => {
  if (!hhmm) return "";
  if (clock !== "12") return hhmm;
  const [h, m] = hhmm.split(":").map(Number);
  const suffix = h < 12 ? "am" : "pm";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")} ${suffix}`;
};

const MON_FIRST = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const weekdayLabels = (weekStarts = "mon") =>
  weekStarts === "sun" ? ["Sun", ...MON_FIRST.slice(0, 6)] : MON_FIRST;

// How many blank cells sit before the 1st of the month.
export const leadingBlanks = (firstDay, weekStarts = "mon") =>
  weekStarts === "sun" ? firstDay.getDay() : (firstDay.getDay() + 6) % 7;

export const eventsForDay = (events, day) => events.filter((e) => e.day === day);

// WMO weather codes → an icon, a short label and a fuller line.
const LOOKS = {
  0: [Sun, "Clear", "Clear sky and sunshine all day", "#F5B301"],
  1: [Sun, "Mostly clear", "Mostly clear with the odd wisp of cloud", "#F5B301"],
  2: [CloudSun, "Sun and cloud", "Sun and cloud taking turns", "#F5B301"],
  3: [Cloud, "Overcast", "Grey and overcast from start to finish", "#94A3B8"],
  45: [CloudFog, "Fog", "Foggy, so allow extra time on the road", "#94A3B8"],
  48: [CloudFog, "Freezing fog", "Freezing fog — icy surfaces underfoot", "#94A3B8"],
  51: [CloudDrizzle, "Light drizzle", "Light drizzle drifting on and off", "#38BDF8"],
  53: [CloudDrizzle, "Drizzle", "Steady drizzle — keep a hood handy", "#38BDF8"],
  55: [CloudDrizzle, "Heavy drizzle", "Heavy drizzle, you will get damp", "#38BDF8"],
  56: [CloudDrizzle, "Freezing drizzle", "Freezing drizzle, slippery underfoot", "#E0F2FE"],
  57: [CloudDrizzle, "Freezing drizzle", "Heavy freezing drizzle, take real care", "#E0F2FE"],
  61: [CloudRain, "Light rain", "Light rain in places", "#38BDF8"],
  63: [CloudRain, "Rain", "Rain for most of the day", "#38BDF8"],
  65: [CloudRain, "Heavy rain", "Heavy rain — umbrella weather", "#38BDF8"],
  66: [CloudRain, "Freezing rain", "Freezing rain, treacherous roads", "#E0F2FE"],
  67: [CloudRain, "Freezing rain", "Heavy freezing rain, best stay in", "#E0F2FE"],
  71: [CloudSnow, "Light snow", "Light snow falling", "#E0F2FE"],
  73: [CloudSnow, "Snow", "Steady snow — wrap up warm", "#E0F2FE"],
  75: [CloudSnow, "Heavy snow", "Heavy snow, travel may suffer", "#E0F2FE"],
  77: [CloudSnow, "Snow grains", "Fine snow grains in the air", "#E0F2FE"],
  80: [CloudRain, "Showers", "Passing showers between bright spells", "#38BDF8"],
  81: [CloudRain, "Sharp showers", "Sharp showers through the day", "#38BDF8"],
  82: [CloudRain, "Downpours", "Torrential showers and sudden downpours", "#38BDF8"],
  85: [CloudSnow, "Snow showers", "Snow showers blowing through", "#E0F2FE"],
  86: [CloudSnow, "Heavy snow showers", "Heavy snow showers, deep in places", "#E0F2FE"],
  95: [CloudLightning, "Storms", "Thunderstorms, heavy at times", "#A855F7"],
  96: [CloudLightning, "Storms with hail", "Thunderstorms with hail — stay under cover", "#A855F7"],
  99: [CloudLightning, "Storms with hail", "Violent storms with large hail", "#A855F7"],
};

export const weatherLook = (code) => {
  const row = LOOKS[code] || LOOKS[3];
  const [Icon, label, blurb, tint] = row;
  return { Icon, label, blurb, tint };
};
